import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, IconButton, Divider, TextField, Paper,
  ToggleButtonGroup, ToggleButton, FormControlLabel, Switch,
  Button, CircularProgress, List, ListItem, ListItemButton,
  ListItemText, Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import SaveIcon from '@mui/icons-material/Save';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LagbasLogo from '../components/LagbasLogo';
import { useQRLogo } from '../hooks/useQRLogo';
import { db, auth } from '../firebase/config';
import {
  collection, query, orderBy, onSnapshot, getDocs,
  addDoc, deleteDoc, doc, getDoc, serverTimestamp,
} from 'firebase/firestore';
import QRCodeLib from 'qrcode';

const ORANGE = '#FF6B00';
const PREVIEW_SIZE = 280;
const EXPORT_SIZE = 1000;
// Corner radius in export pixels when "runda hörn" is on
const CORNER_R = 56;

const switchSx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: ORANGE },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ORANGE },
};

// Convert external URL → base64 data URI (so SVG/PNG exports are self-contained)
async function toDataUri(url) {
  const r = await fetch(url);
  const blob = await r.blob();
  return new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload = e => res(e.target.result);
    reader.onerror = rej;
    reader.readAsDataURL(blob);
  });
}

// Draw a rounded rect path on canvas context
function canvasRoundedRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

// ── SVG preview component ────────────────────────────────────────────────────

function QRSvgEl({ qrData, fgColor, bgColor, transparent, cornerRadius, logoDataUri, size }) {
  if (!qrData) return null;
  const n   = qrData.modules.size;
  const ms  = size / n;
  const ls  = size * 0.2;
  const lx  = (size - ls) / 2;
  const ly  = (size - ls) / 2;
  const pad = size * 0.025;
  // Scale corner radius from export space to preview space
  const cr  = cornerRadius * (size / EXPORT_SIZE);

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ display: 'block' }}
    >
      <defs>
        <clipPath id="qr-clip">
          <rect width={size} height={size} rx={cr} ry={cr} />
        </clipPath>
      </defs>
      <g clipPath="url(#qr-clip)">
        {!transparent && <rect width={size} height={size} fill={bgColor} />}
        {Array.from(qrData.modules.data).map((bit, i) => {
          if (!bit) return null;
          const x = (i % n) * ms;
          const y = Math.floor(i / n) * ms;
          return <rect key={i} x={x} y={y} width={ms} height={ms} fill={fgColor} />;
        })}
        {logoDataUri && (
          <>
            <rect
              x={lx - pad} y={ly - pad}
              width={ls + pad * 2} height={ls + pad * 2}
              fill={bgColor} rx={pad}
            />
            <image href={logoDataUri} x={lx} y={ly} width={ls} height={ls} />
          </>
        )}
      </g>
    </svg>
  );
}

// ── Build SVG string for export ──────────────────────────────────────────────

function buildSvgString(qrData, fgColor, bgColor, transparent, cornerRadius, logoDataUri) {
  const n   = qrData.modules.size;
  const ms  = EXPORT_SIZE / n;
  const ls  = EXPORT_SIZE * 0.2;
  const lx  = (EXPORT_SIZE - ls) / 2;
  const ly  = (EXPORT_SIZE - ls) / 2;
  const pad = EXPORT_SIZE * 0.025;
  const cr  = cornerRadius;

  const parts = [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${EXPORT_SIZE}" height="${EXPORT_SIZE}" viewBox="0 0 ${EXPORT_SIZE} ${EXPORT_SIZE}">`,
    `<defs><clipPath id="qr-clip"><rect width="${EXPORT_SIZE}" height="${EXPORT_SIZE}" rx="${cr}" ry="${cr}"/></clipPath></defs>`,
    `<g clip-path="url(#qr-clip)">`,
  ];
  if (!transparent) parts.push(`<rect width="${EXPORT_SIZE}" height="${EXPORT_SIZE}" fill="${bgColor}"/>`);
  Array.from(qrData.modules.data).forEach((bit, i) => {
    if (!bit) return;
    const x = (i % n) * ms;
    const y = Math.floor(i / n) * ms;
    parts.push(`<rect x="${x}" y="${y}" width="${ms}" height="${ms}" fill="${fgColor}"/>`);
  });
  if (logoDataUri) {
    parts.push(`<rect x="${lx - pad}" y="${ly - pad}" width="${ls + pad * 2}" height="${ls + pad * 2}" fill="${bgColor}" rx="${pad}"/>`);
    parts.push(`<image href="${logoDataUri}" x="${lx}" y="${ly}" width="${ls}" height="${ls}"/>`);
  }
  parts.push('</g></svg>');
  return parts.join('');
}

// ── Main component ───────────────────────────────────────────────────────────

export default function QRKodN() {
  const navigate  = useNavigate();
  const tikabQrUrl = useQRLogo();

  const [companyId, setCompanyId]     = useState(null);
  const [url, setUrl]                 = useState('');
  const [label, setLabel]             = useState('');
  const [mode, setMode]               = useState('positiv');
  const [roundedCorners, setRoundedCorners] = useState(false);
  const [transparent, setTransparent] = useState(false);
  const [qrData, setQrData]           = useState(null);
  const [savedLinks, setSavedLinks]   = useState([]);
  const [saving, setSaving]           = useState(false);
  const [downloading, setDownloading] = useState('');

  // Logo selection: 'tikab' | 'none' | product-id string
  const [logoChoice, setLogoChoice]   = useState('tikab');
  const [products, setProducts]       = useState([]);  // { id, name, imageUrl }
  const [logoDataUri, setLogoDataUri] = useState(null);

  const fgColor      = mode === 'positiv' ? '#000000' : '#ffffff';
  const bgColor      = mode === 'positiv' ? '#ffffff' : '#000000';
  const effectiveUrl = url.trim() || 'https://tikab.se';
  const cornerRadius = roundedCorners ? CORNER_R : 0;

  // Load companyId
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    getDoc(doc(db, 'users', uid))
      .then(snap => setCompanyId(snap.data()?.companyId || null))
      .catch(() => {});
  }, []);

  // Load products (global collection)
  useEffect(() => {
    getDocs(query(collection(db, 'products'), orderBy('name')))
      .then(snap => setProducts(
        snap.docs
          .map(d => ({ id: d.id, name: d.data().name, imageUrl: d.data().imageUrl }))
          .filter(p => p.imageUrl),
      ))
      .catch(() => {});
  }, []);

  // Listen to saved QR links (sorted by label)
  useEffect(() => {
    if (!companyId) return;
    const q = query(
      collection(db, 'companies', companyId, 'qrLinks'),
      orderBy('label', 'asc'),
    );
    return onSnapshot(q, snap =>
      setSavedLinks(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    );
  }, [companyId]);

  // Convert chosen logo URL → data URI whenever selection changes
  useEffect(() => {
    let cancelled = false;
    async function convert() {
      let sourceUrl = null;
      if (logoChoice === 'tikab') sourceUrl = tikabQrUrl;
      else if (logoChoice !== 'none') {
        const p = products.find(p => p.id === logoChoice);
        sourceUrl = p?.imageUrl || null;
      }
      if (!sourceUrl) { if (!cancelled) setLogoDataUri(null); return; }
      try {
        const uri = await toDataUri(sourceUrl);
        if (!cancelled) setLogoDataUri(uri);
      } catch {
        if (!cancelled) setLogoDataUri(null);
      }
    }
    convert();
    return () => { cancelled = true; };
  }, [logoChoice, tikabQrUrl, products]);

  // Generate QR matrix whenever URL changes
  useEffect(() => {
    try {
      setQrData(QRCodeLib.create(effectiveUrl, { errorCorrectionLevel: 'H' }));
    } catch {
      setQrData(null);
    }
  }, [effectiveUrl]);

  async function handleSave() {
    if (!companyId || !url.trim()) return;
    setSaving(true);
    try {
      await addDoc(collection(db, 'companies', companyId, 'qrLinks'), {
        label: label.trim() || url.trim(),
        url: url.trim(),
        createdAt: serverTimestamp(),
      });
      setLabel('');
    } catch (err) {
      console.error('QR link save failed', err);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!companyId) return;
    try { await deleteDoc(doc(db, 'companies', companyId, 'qrLinks', id)); }
    catch (err) { console.error(err); }
  }

  async function downloadPNG() {
    if (!qrData) return;
    setDownloading('png');
    try {
      const qr = QRCodeLib.create(effectiveUrl, { errorCorrectionLevel: 'H' });
      const n  = qr.modules.size;
      const ms = EXPORT_SIZE / n;

      const canvas = document.createElement('canvas');
      canvas.width  = EXPORT_SIZE;
      canvas.height = EXPORT_SIZE;
      const ctx = canvas.getContext('2d');

      // Apply rounded corner clip
      if (cornerRadius > 0) {
        canvasRoundedRect(ctx, 0, 0, EXPORT_SIZE, EXPORT_SIZE, cornerRadius);
        ctx.clip();
      }

      if (!transparent) {
        ctx.fillStyle = bgColor;
        ctx.fillRect(0, 0, EXPORT_SIZE, EXPORT_SIZE);
      }

      ctx.fillStyle = fgColor;
      Array.from(qr.modules.data).forEach((bit, i) => {
        if (!bit) return;
        ctx.fillRect((i % n) * ms, Math.floor(i / n) * ms, ms, ms);
      });

      if (logoDataUri) {
        const ls  = EXPORT_SIZE * 0.2;
        const lx  = (EXPORT_SIZE - ls) / 2;
        const ly  = (EXPORT_SIZE - ls) / 2;
        const pad = EXPORT_SIZE * 0.025;
        ctx.fillStyle = bgColor;
        canvasRoundedRect(ctx, lx - pad, ly - pad, ls + pad * 2, ls + pad * 2, pad);
        ctx.fill();
        const img = new Image();
        await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = logoDataUri; });
        ctx.drawImage(img, lx, ly, ls, ls);
      }

      const a = document.createElement('a');
      a.download = 'qr-tikab.png';
      a.href = canvas.toDataURL('image/png');
      a.click();
    } catch (err) {
      console.error('PNG export failed', err);
    } finally {
      setDownloading('');
    }
  }

  function downloadSVG() {
    if (!qrData) return;
    setDownloading('svg');
    try {
      const svgStr = buildSvgString(qrData, fgColor, bgColor, transparent, cornerRadius, logoDataUri);
      const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
      const a = document.createElement('a');
      a.download = 'qr-tikab.svg';
      a.href = URL.createObjectURL(blob);
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      console.error('SVG export failed', err);
    } finally {
      setDownloading('');
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>

      {/* Header */}
      <Box sx={{
        bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', gap: 2,
      }}>
        <LagbasLogo />
        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
        <IconButton color="inherit" size="small" onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="subtitle1" fontWeight="bold">QR-Kod<span style={{ color: ORANGE }}>N</span></Typography>
          <Typography variant="caption" sx={{ color: '#aaa' }}>Generera QR-koder med Tikab-profil</Typography>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ display: 'flex', minHeight: 'calc(100vh - 56px)' }}>

        {/* ── Left panel ── */}
        <Box sx={{
          width: 300, flexShrink: 0,
          borderRight: '1px solid', borderColor: 'divider',
          p: 2.5, display: 'flex', flexDirection: 'column', gap: 2,
          overflowY: 'auto',
        }}>

          {/* URL + save */}
          <TextField
            label="URL"
            size="small"
            fullWidth
            value={url}
            onChange={e => setUrl(e.target.value)}
            placeholder="https://..."
            onKeyDown={e => { if (e.key === 'Enter') handleSave(); }}
          />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              label="Namn (valfritt)"
              size="small"
              sx={{ flex: 1 }}
              value={label}
              onChange={e => setLabel(e.target.value)}
              placeholder="Beskriv URL:en"
            />
            <Tooltip title="Spara i listan">
              <span>
                <IconButton
                  size="small"
                  onClick={handleSave}
                  disabled={saving || !url.trim()}
                  sx={{
                    mt: '2px', color: ORANGE, border: `1px solid ${ORANGE}`, borderRadius: 1,
                    '&:disabled': { opacity: 0.4 },
                  }}
                >
                  {saving ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {/* Saved links */}
          {savedLinks.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                Sparade URL:er — A–Ö
              </Typography>
              <Paper variant="outlined" sx={{ maxHeight: 180, overflowY: 'auto' }}>
                <List dense disablePadding>
                  {savedLinks.map(link => (
                    <ListItem
                      key={link.id}
                      disablePadding
                      secondaryAction={
                        <IconButton size="small" edge="end" onClick={() => handleDelete(link.id)}
                          sx={{ color: '#bbb', '&:hover': { color: '#d32f2f' } }}>
                          <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      }
                    >
                      <ListItemButton
                        selected={url === link.url}
                        onClick={() => { setUrl(link.url); setLabel(link.label || ''); }}
                        sx={{ pr: 4 }}
                      >
                        <ListItemText
                          primary={link.label || link.url}
                          secondary={link.label ? link.url : undefined}
                          primaryTypographyProps={{ fontSize: '0.8rem', noWrap: true }}
                          secondaryTypographyProps={{ fontSize: '0.68rem', noWrap: true }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Box>
          )}

          <Divider />

          {/* Variant */}
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>Variant</Typography>
            <ToggleButtonGroup
              value={mode} exclusive size="small" fullWidth
              onChange={(_, v) => { if (v) setMode(v); }}
            >
              <ToggleButton value="positiv" sx={{ flex: 1, fontSize: '0.75rem', textTransform: 'none' }}>Positiv</ToggleButton>
              <ToggleButton value="negativ" sx={{ flex: 1, fontSize: '0.75rem', textTransform: 'none' }}>Negativ</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <FormControlLabel
            control={<Switch size="small" checked={roundedCorners} onChange={e => setRoundedCorners(e.target.checked)} sx={switchSx} />}
            label={<Typography variant="body2">Runda hörn</Typography>}
          />

          <FormControlLabel
            control={<Switch size="small" checked={transparent} onChange={e => setTransparent(e.target.checked)} sx={switchSx} />}
            label={<Typography variant="body2">Transparent bakgrund</Typography>}
          />

          <Divider />

          {/* Logo picker */}
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              Logotyp i mitten
            </Typography>
            {/* Built-in options */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              {[
                { id: 'none',  label: 'Ingen' },
                { id: 'tikab', label: 'Tikab QR' },
              ].map(opt => (
                <Box
                  key={opt.id}
                  onClick={() => setLogoChoice(opt.id)}
                  sx={{
                    px: 1.5, py: 0.5, borderRadius: 1, cursor: 'pointer', fontSize: '0.75rem',
                    border: '1px solid',
                    borderColor: logoChoice === opt.id ? ORANGE : 'divider',
                    color: logoChoice === opt.id ? ORANGE : 'text.secondary',
                    fontWeight: logoChoice === opt.id ? 700 : 400,
                    bgcolor: logoChoice === opt.id ? `${ORANGE}15` : 'transparent',
                  }}
                >
                  {opt.label}
                </Box>
              ))}
            </Box>
            {/* Product logos */}
            {products.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {products.map(p => (
                  <Tooltip key={p.id} title={p.name}>
                    <Box
                      onClick={() => setLogoChoice(p.id)}
                      sx={{
                        width: 44, height: 44, borderRadius: 1, overflow: 'hidden',
                        border: '2px solid',
                        borderColor: logoChoice === p.id ? ORANGE : 'divider',
                        cursor: 'pointer', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: 'background.paper',
                        '&:hover': { borderColor: ORANGE },
                      }}
                    >
                      <Box
                        component="img"
                        src={p.imageUrl}
                        alt={p.name}
                        sx={{ width: 36, height: 36, objectFit: 'contain' }}
                      />
                    </Box>
                  </Tooltip>
                ))}
              </Box>
            )}
          </Box>

          <Divider />

          {/* Download buttons */}
          <Button
            variant="contained" fullWidth size="small"
            startIcon={downloading === 'png' ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
            onClick={downloadPNG}
            disabled={!!downloading || !qrData}
            sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#c44d00' }, textTransform: 'none' }}
          >
            Ladda ner PNG (1000×1000)
          </Button>
          <Button
            variant="outlined" fullWidth size="small"
            startIcon={downloading === 'svg' ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
            onClick={downloadSVG}
            disabled={!!downloading || !qrData}
            sx={{ borderColor: ORANGE, color: ORANGE, textTransform: 'none' }}
          >
            Ladda ner SVG
          </Button>
        </Box>

        {/* ── Preview ── */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
              Förhandsgranskning
            </Typography>
            <Box sx={{
              p: '16px', borderRadius: 2,
              border: '1px solid', borderColor: 'divider',
              display: 'inline-block',
              bgcolor: transparent ? 'transparent' : (mode === 'positiv' ? '#f2f2f2' : '#2a2a2a'),
              backgroundImage: transparent
                ? 'repeating-conic-gradient(rgba(0,0,0,0.08) 0% 25%, white 0% 50%) 0 0 / 16px 16px'
                : 'none',
            }}>
              <QRSvgEl
                qrData={qrData}
                fgColor={fgColor}
                bgColor={bgColor}
                transparent={transparent}
                cornerRadius={cornerRadius}
                logoDataUri={logoDataUri}
                size={PREVIEW_SIZE}
              />
            </Box>
            {!url.trim() && (
              <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
                Ange en URL för att generera
              </Typography>
            )}
            {url.trim() && (
              <Typography variant="caption" color="text.secondary" display="block" mt={1.5}
                sx={{ maxWidth: 320, wordBreak: 'break-all' }}>
                {effectiveUrl}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
